"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { CONSENT_CATEGORIES, OPTIONAL_CONSENT_CATEGORIES, advertisingConsentContract, consentCookieName, customCodeAllowed, decodeConsentCookie, encodeConsentCookie, normalizePrivacyConfig, resolveConsentState } from "@/lib/privacy/consent";

import ConsentInterface from "./ConsentInterface";
import BrowserAdDiagnostics from "../ads/BrowserAdDiagnostics";
import { CONSENT_COOKIE_MAX_AGE_SECONDS, hasCurrentAnalyticsConsent, hasCurrentAdvertisingConsent } from "@/lib/privacy/consent";

const ConsentContext = createContext({ contract: advertisingConsentContract(null), state: null, openPreferences: () => {} });
export const useConsent = () => useContext(ConsentContext);

function readCookie(name) {
  const row = document.cookie.split("; ").find(item => item.startsWith(`${name}=`));
  return row ? decodeConsentCookie(row.slice(name.length + 1)) : null;
}

function writeCookie(name, value) {
  document.cookie = `${name}=${encodeConsentCookie(value)}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
}

function GatedCustomCode({ html, category, state, location: target }) {
  const hostRef = useRef(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !html || !customCodeAllowed(category, state)) return;
    const template = document.createElement("template");
    template.innerHTML = html;
    const fragment = document.createDocumentFragment();
    for (const node of [...template.content.childNodes]) {
      if (node.nodeName === "SCRIPT") {
        const script = document.createElement("script");
        for (const attribute of [...node.attributes]) script.setAttribute(attribute.name, attribute.value);
        script.textContent = node.textContent;
        fragment.appendChild(script);
      } else fragment.appendChild(node.cloneNode(true));
    }
    host.appendChild(fragment);
    return () => host.replaceChildren();
  }, [category, html, state]);
  return <span ref={hostRef} hidden data-consent-custom-code={target} />;
}


export default function ConsentRuntime({ publicationId, publicationName, privacy, presentation = {}, customCode = {}, children }) {
  const pathname = usePathname();
  const config = useMemo(() => normalizePrivacyConfig(privacy), [privacy]);
  const cookieName = consentCookieName(publicationId);
  // Never expose an old publication or policy's state during the render before effects run.
  const contextKey = JSON.stringify([publicationId, config]);
  const [snapshot, setSnapshot] = useState(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [error, setError] = useState(false);
  const [draft, setDraft] = useState({});
  const analyticsBlocked = useRef(true);
  const advertisingBlocked = useRef(true);
  const ready = snapshot?.key === contextKey;
  const state = ready ? snapshot.state : resolveConsentState(config, null, { publicationId });
  const enabled = Boolean(publicationId && config.enabled && config.policyVersion);
  const visible = enabled && pathname !== "/activate-staff";
  useEffect(() => {
    let stored = null;
    try { stored = readCookie(cookieName); } catch { /* Fail closed if cookies are unavailable. */ }
    const next = resolveConsentState(config, stored, { publicationId, globalPrivacyControl: navigator.globalPrivacyControl === true });
    analyticsBlocked.current = !hasCurrentAnalyticsConsent(config, stored, publicationId);
    advertisingBlocked.current = !hasCurrentAdvertisingConsent(config, stored, publicationId);
    setSnapshot({ key: contextKey, state: next, stored });
    setDraft(Object.fromEntries(OPTIONAL_CONSENT_CATEGORIES.map(category => [category, next.categories[category] === "GRANTED"])));
    setPreferencesOpen(false); setError(false);
  }, [config, contextKey, cookieName, publicationId]);

  const persist = useCallback(choices => {
    if (!enabled || !ready) return;
    // Synchronous fail-closed barrier, including when writing the cookie fails.
    analyticsBlocked.current = true;
    advertisingBlocked.current = true;
    const categories = Object.fromEntries(CONSENT_CATEGORIES.map(category => [category, category === "NECESSARY" ? "GRANTED" : config.categories.includes(category) && choices[category] ? "GRANTED" : "DENIED"]));
    const stored = { publicationId, policyVersion: config.policyVersion, timestamp: new Date().toISOString(), categories };
    try {
      writeCookie(cookieName, stored);
      if (JSON.stringify(readCookie(cookieName)) !== JSON.stringify(stored)) throw new Error("Cookie write failed");
      const next = resolveConsentState(config, stored, { publicationId, globalPrivacyControl: navigator.globalPrivacyControl === true });
      analyticsBlocked.current = !hasCurrentAnalyticsConsent(config, stored, publicationId);
      advertisingBlocked.current = !hasCurrentAdvertisingConsent(config, stored, publicationId);
      setSnapshot({ key: contextKey, state: next, stored });
      setDraft(Object.fromEntries(OPTIONAL_CONSENT_CATEGORIES.map(category => [category, next.categories[category] === "GRANTED"])));
      setPreferencesOpen(false); setError(false);
      window.dispatchEvent(new CustomEvent("surgedome:consent-changed", { detail: advertisingConsentContract(next) }));
    } catch {
      // Do not claim a grant or withdrawal was persisted when storage failed.
      setSnapshot({ key: contextKey, state: resolveConsentState(config, null, { publicationId }) });
      setError(true);
      window.dispatchEvent(new CustomEvent("surgedome:consent-changed", { detail: advertisingConsentContract(null) }));
    }
  }, [enabled, ready, config, publicationId, cookieName, contextKey]);
  const all = value => persist(Object.fromEntries(config.categories.map(category => [category, value])));
  const close = useCallback(() => setPreferencesOpen(false), []);
  const open = () => {
    if (!visible || !ready) return;
    setDraft(Object.fromEntries(OPTIONAL_CONSENT_CATEGORIES.map(category => [category, state.categories[category] === "GRANTED"])));
    setPreferencesOpen(true);
  };
  const contract = advertisingConsentContract(state);
  const advertisingCurrent = enabled && ready && hasCurrentAdvertisingConsent(config, snapshot?.stored, publicationId);
  contract.advertisingAllowed = contract.advertisingAllowed && advertisingCurrent;
  contract.personalizedAdvertisingAllowed = contract.personalizedAdvertisingAllowed && advertisingCurrent;
  useEffect(() => {
    if (!advertisingCurrent) return;
    const expires = Date.parse(snapshot.stored.timestamp) + CONSENT_COOKIE_MAX_AGE_SECONDS * 1000;
    // Browser timers cannot represent the whole policy lifetime in one delay.
    const timer = setTimeout(() => setSnapshot(current => current ? { ...current } : current), Math.min(2147483647, Math.max(1, expires - Date.now())));
    return () => clearTimeout(timer);
  }, [advertisingCurrent, snapshot]);
  const canRequestAdvertising = useCallback(() => {
    if (!enabled || !ready || advertisingBlocked.current) return false;
    try { return hasCurrentAdvertisingConsent(config, readCookie(cookieName), publicationId); }
    catch { return false; }
  }, [enabled, ready, config, cookieName, publicationId]);
  const canTrackAnalytics = useCallback(() => {
    if (!enabled || !ready || analyticsBlocked.current) return false;
    try { return hasCurrentAnalyticsConsent(config, readCookie(cookieName), publicationId); }
    catch { return false; }
  }, [enabled, ready, config, cookieName, publicationId]);
  return <ConsentContext.Provider value={{ contract, state, analyticsCollectionEnabled: enabled, canTrackAnalytics, canRequestAdvertising, openPreferences: open }}>
    {children}
    <BrowserAdDiagnostics publicationId={publicationId} advertisingAllowed={contract.advertisingAllowed} />
    {visible && ready && <ConsentInterface publicationName={publicationName} presentation={presentation} config={config} known={state.consentKnown} open={preferencesOpen} draft={draft} setDraft={setDraft} onOpen={open} onClose={close} onAccept={() => all(true)} onReject={() => all(false)} onSave={() => persist(draft)} error={error} />}
    {pathname !== "/activate-staff" && <GatedCustomCode html={customCode.header} category={customCode.headerCategory} state={state} location="header" />}
    {pathname !== "/activate-staff" && <GatedCustomCode html={customCode.footer} category={customCode.footerCategory} state={state} location="footer" />}
  </ConsentContext.Provider>;
}
